/* eslint-disable max-len */
import { useFormContext } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { GET_MENU_SECTION_BY_LOCATION } from "gql";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTranslate } from "react-admin";

export default function AddServicesAndAddOnsFromArtistCalendar({
  locationsData,
  cart,
  setCart
}: any) {
  const translate = useTranslate();
  const form = useFormContext();
  const selectedLocation = form.watch("location");
  const menuId = locationsData?.find(
    (item: any) => item?.id === selectedLocation?.id
  )?.menu?.id;

  const { data: menu } = useQuery(GET_MENU_SECTION_BY_LOCATION, {
    variables: {
      where: {
        menu_id: {
          equals: menuId
        }
      },
      orderBy: [
        {
          section_position: "asc"
        }
      ],
      menuSectionServicesOrderBy2: [
        {
          section_position: "asc"
        }
      ]
    },
    skip: !menuId,
    fetchPolicy: "no-cache"
  });

  function isSelectedService(
    cart: any,
    _serviceId: string,
    _personIndex: number
  ): boolean {
    return !!cart.services?.find((service: any) => {
      return (
        service.personIndex === _personIndex &&
        service.service.id === _serviceId
      );
    });
  }

  function selectService(
    _service: any,
    _personIndex: number,
    _add_ons?: string[],
    _is_add_on?: boolean
  ): void {
    let newCartServices = [];

    if (isSelectedService(cart, _service.id, _personIndex)) {
      newCartServices = cart.services.filter(
        (service: any, index: number, array: any) =>
          !array.find(
            () =>
              service.personIndex === _personIndex &&
              service.service.id === _service.id
          )
      );
    } else {
      newCartServices = [
        ...cart.services,
        {
          service: _service,
          personIndex: _personIndex,
          addOns: _add_ons
        }
      ];
    }

    setCart({
      ...cart,
      services: newCartServices
    });
  }
  function selectAddon(
    _service: any,
    _personIndex: number,
    _add_on_id: string
  ): void {
    let newCartServices = [];

    const findService = cart.services?.find((service: any) => {
      return (
        service?.personIndex === _personIndex &&
        service?.service?.id === _service?.id
      );
    });

    if (findService?.addOns?.includes(_add_on_id)) {
      newCartServices = cart.services.map(
        (service: any, index: number, array: any) => {
          if (
            array.find(
              () =>
                service.personIndex === _personIndex &&
                service.service.id === _service.id
            )
          ) {
            return {
              ...service,
              addOns: service?.addOns?.filter(
                (item: any) => item !== _add_on_id
              )
            };
          } else {
            return service;
          }
        }
      );
    } else {
      newCartServices = cart.services.map(
        (service: any, index: number, array: any) => {
          if (
            array.find(
              () =>
                service?.personIndex === _personIndex &&
                service?.service?.id === _service?.id
            )
          ) {
            return {
              ...service,
              addOns: [...service?.addOns, _add_on_id]
            };
          } else {
            return service;
          }
        }
      );
    }

    setCart({
      ...cart,
      services: newCartServices
    });
  }

  return (
    <Box
      className="w-full h-full mb-6"
      sx={{
        "& .MuiAccordion-root": {
          boxShadow: "none !important",
          backgroundColor: "rgba(0, 0, 0, 0.04) !important"
        },
        "& .MuiAccordionSummary-root": {
          minHeight: "36px !important"
        },
        "& .MuiAccordionDetails-root": {
          padding: "8px 16px !important"
        },
        "&.Mui-expanded": {
          minHeight: "36px !important",
          margin: "0px !important"
        },
        "& .MuiAccordionSummary-content": {
          margin: "4px 0px !important"
        }
      }}
    >
      {!selectedLocation?.id && (
        <div className="mb-4">
          {translate("appointment.please_select_the_location_first")}
        </div>
      )}
      {menu?.menuSections?.map((menuSection: any, index: number) => (
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              "&.Mui-expanded": {
                minHeight: "36px !important",
                margin: "0px !important"
              }
            }}
          >
            <div className="text-[16px] font-[600]">
              {translate("appointment.section")}: {menuSection.name}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div key={index} className="w-full">
              {menuSection?.menu_section_services?.map(
                (option: any, i: number) => {
                  const isServiceAdded = isSelectedService(
                    cart,
                    option?.service?.id,
                    0
                  );
                  return (
                    <div
                      key={i}
                      className="w-full bg-[rgba(0,0,0,0.04)] p-3 rounded-lg mb-4"
                    >
                      <div className="text-[18px] font-[600]">
                        {translate("appointment.service")}:
                      </div>
                      <div className="w-full flex justify-between items-center py-1 mb-2">
                        <div>
                          <div>{option?.service?.name}</div>
                          <div>
                            ${option.service?.price} •{" "}
                            {option.service?.duration} mins
                          </div>
                        </div>
                        <Chip
                          variant={isServiceAdded ? "filled" : "outlined"}
                          color={isServiceAdded ? "primary" : "default"}
                          label={
                            isServiceAdded
                              ? translate("appointment.added")
                              : translate("appointment.add")
                          }
                          icon={isServiceAdded ? <RemoveIcon /> : <AddIcon />}
                          onClick={() => {
                            selectService(
                              option.service,
                              0, // first person
                              [] // empty add-ons
                            );
                          }}
                        />
                      </div>
                      {option?.service?.add_ons?.length > 0 && (
                        <Box>
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                              sx={{
                                "&.Mui-expanded": {
                                  minHeight: "36px !important",
                                  margin: "0px !important"
                                }
                              }}
                            >
                              <div className="text-[16px] font-[600]">
                                {translate("appointment.addons")}:
                              </div>
                            </AccordionSummary>
                            <AccordionDetails>
                              {option?.service?.add_ons?.map((addOn: any) => {
                                const isSelected = cart?.services?.find(
                                  (item: any) => {
                                    return (
                                      item?.personIndex === 0 &&
                                      item?.service?.id ===
                                        option?.service?.id &&
                                      item?.addOns?.includes(
                                        addOn.core_service.id
                                      )
                                    );
                                  }
                                );
                                return (
                                  <div
                                    key={addOn?.id}
                                    className="w-full flex justify-between items-center py-1 border-b border-[rgba(0,0,0,0.06)] last:border-b-0"
                                  >
                                    <div>
                                      <div>{addOn?.core_service?.name}</div>
                                      <div>
                                        ${addOn?.core_service?.price} •{" "}
                                        {addOn?.core_service?.duration} mins
                                      </div>
                                    </div>
                                    <Chip
                                      variant={
                                        isSelected ? "filled" : "outlined"
                                      }
                                      color={isSelected ? "primary" : "default"}
                                      label={
                                        isSelected
                                          ? translate("appointment.added")
                                          : translate("appointment.add")
                                      }
                                      icon={
                                        isSelected ? (
                                          <RemoveIcon />
                                        ) : (
                                          <AddIcon />
                                        )
                                      }
                                      onClick={() => {
                                        selectAddon(
                                          option.service,
                                          0,
                                          addOn.core_service.id
                                        );
                                      }}
                                    />
                                  </div>
                                );
                              })}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
